import React from 'react';
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { LevelBadge as LevelBadgeData } from '../../types/auth';
import { useTheme } from '../../context/ThemeContext';
import { uiFont } from '../../styles/agapellaFonts';
import {
  LEVEL_BADGE_SIZES,
  LEVEL_BADGE_SIZER_LABEL,
  LEVEL_BADGE_SLOT_HEIGHTS,
  resolveLevelBadgeAsset,
  shouldReserveLevelBadgeSlot,
  type LevelBadgeLoadStatus,
  type LevelBadgeSize,
} from './levelBadgeContract';

export {
  LEVEL_BADGE_ASSETS,
  LEVEL_BADGE_LABELS,
  LEVEL_BADGE_SIZES,
  LEVEL_BADGE_SLOT_HEIGHTS,
  resolveLevelBadgeAsset,
  resolveLevelBadgeLabel,
} from './levelBadgeContract';


interface Props {
  badge?: LevelBadgeData | null;
  /** comments/chat = 40, card/list = 48, profile = 56. */
  size?: LevelBadgeSize;
  /**
   * Loading/empty/error keep one stable invisible slot; disabled removes the
   * surface entirely. Omit only for already-resolved static badges.
   */
  status?: LevelBadgeLoadStatus;
  style?: any;
}

/**
 * Variant B: an informational medallion and the canonical localized level name
 * on a soft translucent pill. Invalid levels render nothing instead of
 * pretending the user is an «Искатель».
 */
export const LevelBadge: React.FC<Props> = ({ badge, size = 'compact', status, style }) => {
  const { isDark } = useTheme();
  const backendName = badge?.name?.trim();
  const asset = resolveLevelBadgeAsset(badge?.level);
  const name = backendName && asset ? backendName : null;
  const resolvedStatus: LevelBadgeLoadStatus = status ?? (name && asset ? 'ready' : 'disabled');
  const showBadge = resolvedStatus === 'ready' && !!name && !!asset;
  if (!showBadge && !shouldReserveLevelBadgeSlot(resolvedStatus)) return null;

  const medallionSize = LEVEL_BADGE_SIZES[size];

  return (
    <View
      style={[styles.slot, { minHeight: LEVEL_BADGE_SLOT_HEIGHTS[size] }, style]}
      aria-hidden={!showBadge}
      accessibilityElementsHidden={!showBadge}
      importantForAccessibility={showBadge ? 'auto' : 'no-hide-descendants'}
    >
      {/*
       * CORE-707 — the invisible longest-label specimen owns layout. The real
       * pill is absolutely positioned over it, so loading -> ready/empty/error
       * never changes x/y/width/height. It contains no image and is hidden from
       * accessibility, so it cannot masquerade as a real level.
       */}
      <View
        style={[styles.wrap, styles.sizer]}
        aria-hidden
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <View style={{ width: medallionSize, height: medallionSize }} />
        <Text style={styles.name}>{LEVEL_BADGE_SIZER_LABEL}</Text>
      </View>

      {showBadge ? (
        <View
          style={[
            styles.wrap,
            styles.visibleBadge,
            isDark ? styles.wrapDark : styles.wrapLight,
          ]}
          accessibilityRole="text"
          accessibilityLabel={`Уровень: ${name}`}
        >
          <Image
            source={asset}
            resizeMode="contain"
            style={{ width: medallionSize, height: medallionSize }}
            accessibilityIgnoresInvertColors
          />
          <Text style={[styles.name, isDark && styles.nameDark]}>{name}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  slot: {
    position: 'relative',
    minWidth: 0,
    maxWidth: '100%',
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  wrap: {
    minWidth: 0,
    maxWidth: '100%',
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 4,
    paddingRight: 14,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 999,
  } as any,
  sizer: {
    opacity: 0,
    pointerEvents: 'none',
    borderColor: 'transparent',
    backgroundColor: 'transparent',
  },
  visibleBadge: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  wrapLight: {
    borderColor: 'rgba(226, 125, 50, 0.24)',
    backgroundColor: 'rgba(255, 246, 236, 0.78)',
  },
  wrapDark: {
    borderColor: 'rgba(232, 128, 44, 0.25)',
    backgroundColor: 'rgba(50, 35, 25, 0.58)',
  },
  name: {
    minWidth: 0,
    flexShrink: 1,
    ...uiFont(400),
    fontSize: 13,
    lineHeight: 17,
    ...uiFont(500),
    letterSpacing: 0,
    color: '#2C2521',
    ...(Platform.OS === 'web' && { overflowWrap: 'anywhere', wordBreak: 'break-word' }),
  } as any,
  nameDark: {
    color: '#FFF9F2',
  },
});

export default LevelBadge;
